import React, { useState, useEffect } from 'react';
import { Plus, Search, Eye, Edit, Package, Phone, X, LayoutGrid, List, Sprout, Calendar, IndianRupee } from 'lucide-react';
import { Sale, Customer, Plant, SaleItem } from '../../types';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { orderService, customerService, plantService } from '../../services/firebase';
import ReceiptGenerator from '../receipt/ReceiptGenerator';
import { printReceiptWithLogo } from '../../utils/receiptUtils';
import OrderDetailsModal from './OrderDetailsModal';

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<Sale[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [formData, setFormData] = useState({
    customerId: '',
    plantId: '',
    quantity: '',
    amountPaid: '',
    expectedDeliveryDate: '',
  });
  const [quantityInputMode, setQuantityInputMode] = useState<'quantity' | 'trays'>('quantity');
  const [traysInput, setTraysInput] = useState('');
  const [customPricePerPlant, setCustomPricePerPlant] = useState<string>('');
  // Cart items for multi-plant orders
  const [cartItems, setCartItems] = useState<SaleItem[]>([]);
  const [showAddFarmerForm, setShowAddFarmerForm] = useState(false);
  const [newFarmerData, setNewFarmerData] = useState({
    name: '',
    phone: '',
    address: '',
    distanceKm: '',
  });
  const [farmerSearchTerm, setFarmerSearchTerm] = useState('');
  const [showAddPlantForm, setShowAddPlantForm] = useState(false);
  const [newPlantData, setNewPlantData] = useState({
    name: '',
    brand: '',
    variety: '',
    pricePerPlant: '',
    growthDuration: '',
    seedsPerTray: '126',
    seedsProvider: 'Company' as 'Farmer' | 'Company',
  });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Sale | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [amountPaidError, setAmountPaidError] = useState<string | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState<{
    order: Sale;
    customer: Customer;
    payments: any[];
    receiptType: 'order' | 'payment' | 'completion';
    plantDetails?: Plant;
  } | null>(null);
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState<Sale | null>(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  const statusOptions = [
    { id: '1', name: 'Pending', description: 'Order received', order: 1 },
    { id: '2', name: 'In Progress', description: 'Order in production', order: 2 },
    { id: '3', name: 'Ready', description: 'Order ready for delivery', order: 3 },
    { id: '4', name: 'Delivered', description: 'Order delivered', order: 4 },
    { id: '5', name: 'Complete', description: 'Payment completed', order: 5 },
    { id: '6', name: 'Cancelled', description: 'Order cancelled', order: 6 },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [customersData, plantsData, ordersData] = await Promise.all([
          customerService.getAllCustomers(),
          plantService.getAllPlants(),
          orderService.getAllOrders()
        ]);
        
        setCustomers(customersData);
        setPlants(plantsData);
        setOrders(ordersData);
        
      } catch (error) {
        console.error('Error fetching data:', error);
        // You might want to show an error message to the user here
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that there are items in the cart
    if (cartItems.length === 0) {
      alert('Please add at least one plant to the order.');
      return;
    }

    // Calculate cart totals
    const cartTotals = calculateCartTotal();

    // Validate amount paid against cart total
    const amountPaid = parseFloat(formData.amountPaid) || 0;
    if (amountPaid > cartTotals.totalAmount) {
      setAmountPaidError(`Amount paid cannot exceed total amount (₹${cartTotals.totalAmount.toLocaleString()})`);
      return;
    }
    if (amountPaid > 0 && amountPaid < 500) {
      setAmountPaidError('Minimum amount paid must be ₹500');
      return;
    }

    // Validate new farmer data if adding new farmer
    if (showAddFarmerForm) {
      if (!newFarmerData.name || !newFarmerData.phone || !newFarmerData.address) {
        alert('Please fill in all required farmer details.');
        return;
      }

      const phoneError = validatePhoneNumber(newFarmerData.phone);
      if (phoneError) {
        alert(phoneError);
        return;
      }

      const distanceError = validateDistance(newFarmerData.distanceKm);
      if (distanceError) {
        alert(distanceError);
        return;
      }
    }

    let selectedCustomer = customers.find(c => c.id === formData.customerId);

    // If creating a new farmer, do it first
    if (showAddFarmerForm) {
      try {
        setLoading(true);
        const customerData = {
          name: newFarmerData.name,
          phone: newFarmerData.phone,
          address: newFarmerData.address,
          distanceKm: newFarmerData.distanceKm ? parseFloat(newFarmerData.distanceKm) : undefined
        };
        const newCustomerId = await customerService.addCustomer(customerData);

        // Create the customer object with proper Firestore timestamp
        const newCustomer = {
          id: newCustomerId,
          ...customerData,
          registrationDate: new Date() as any,
          totalOrders: 0,
          totalSpent: 0,
        };

        // Update the customers list
        setCustomers([newCustomer, ...customers]);

        // Update form data to use the new customer ID
        setFormData({ ...formData, customerId: newCustomerId });

        // Set the selected customer for order creation
        selectedCustomer = newCustomer;

      } catch (error) {
        console.error('Error creating new customer:', error);
        alert('Error creating new farmer. Please try again.');
        setLoading(false);
        return;
      }
    }

    if (!selectedCustomer) {
      alert('Please select a farmer.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const orderData = {
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        items: cartItems,
        totalAmount: cartTotals.totalAmount,
        amountPaid: amountPaid,
        remainingAmount: cartTotals.totalAmount - amountPaid,
        totalTrays: cartTotals.totalTrays,
        ...(formData.expectedDeliveryDate && {
          expectedDeliveryDate: Timestamp.fromDate(new Date(formData.expectedDeliveryDate))
        }),
      } as any;

      const { docId, orderId } = await orderService.addOrder(orderData);

      const newOrder: Sale = {
        id: docId,
        orderId: orderId,
        ...orderData,
        status: { id: '0', name: 'SeedingPending', description: 'Awaiting seeding date', order: 0 },
        orderDate: new Date() as any,
        expectedDeliveryDate: formData.expectedDeliveryDate ? Timestamp.fromDate(new Date(formData.expectedDeliveryDate)) as any : undefined,
        createdAt: new Date() as any,
        updatedAt: new Date() as any,
      };

      setOrders([newOrder, ...orders]);

      // Generate receipt for the new order
      if (selectedCustomer) {
        setReceiptData({
          order: newOrder,
          customer: selectedCustomer,
          payments: [],
          receiptType: 'order',
        });
        setShowReceipt(true);
      }

      resetForm();
      setLoading(false); // Clear loading state after successful order creation
    } catch (error) {
      console.error('Error creating order:', error);

      // Show user-friendly error message
      if (error instanceof Error && error.message.includes('index')) {
        alert('Error: Please create the required database index. Click the link in the console for instructions.');
      } else {
        alert('Error creating order. Please try again.');
      }
      setLoading(false); // Clear loading state on error
    }
  };

  const resetForm = () => {
    setFormData({
      customerId: '',
      plantId: '',
      quantity: '',
      amountPaid: '',
      expectedDeliveryDate: '',
    });
    setNewFarmerData({
      name: '',
      phone: '',
      address: '',
      distanceKm: '',
    });
    setNewPlantData({
      name: '',
      brand: '',
      variety: '',
      pricePerPlant: '',
      growthDuration: '',
      seedsPerTray: '126',
      seedsProvider: 'Company',
    });
    setCartItems([]);
    setFarmerSearchTerm('');
    setShowForm(false);
    setShowAddFarmerForm(false);
    setShowAddPlantForm(false);
    setAmountPaidError(null);
    setQuantityInputMode('quantity');
    setTraysInput('');
    setCustomPricePerPlant('');
  };

  const handleStatusChange = async () => {
    if (!selectedOrder || !newStatus) return;
    
    const status = statusOptions.find(s => s.id === newStatus);
    if (!status) return;

    try {
      await orderService.updateOrderStatus(selectedOrder.id, status);
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === selectedOrder.id 
          ? { ...order, status } 
          : order
      ));
      
      setShowStatusModal(false);
      setSelectedOrder(null);
      setNewStatus('');
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const openStatusModal = (order: Sale) => {
    setSelectedOrder(order);
    setNewStatus(order.status.id);
    setShowStatusModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'seedingpending':
        return 'bg-orange-100 text-orange-800 border border-orange-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'in_progress':
      case 'in progress':
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'ready':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'delivered':
        return 'bg-gray-100 text-gray-800 border border-gray-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const formatDate = (dateValue: any): Date => {
    if (!dateValue) return new Date();
    
    // If it's a Firestore Timestamp
    if (dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }
    
    // If it's already a Date object
    if (dateValue instanceof Date) {
      return dateValue;
    }
    
    // If it's a number (timestamp)
    if (typeof dateValue === 'number') {
      return new Date(dateValue);
    }
    
    // Try to create a Date from the value
    return new Date(dateValue);
  };

  const filteredOrders = orders
    .filter(order => {
      // Exclude deleted orders from the list
      if (order.status.name.toLowerCase() === 'deleted') {
        return false;
      }

      let matchesSearch = true;

      if (searchTerm) {
        const lowerSearchTerm = searchTerm.toLowerCase().trim();

        // Search across multiple fields
        const matchesCustomerName = order.customerName?.toLowerCase().includes(lowerSearchTerm) || false;
        const matchesOrderId = order.orderId?.toLowerCase().includes(lowerSearchTerm) || false;

        // Search in order items (for multi-plant orders)
        let matchesPlantInfo = false;
        if (order.items && order.items.length > 0) {
          matchesPlantInfo = order.items.some(item =>
            item.plantName?.toLowerCase().includes(lowerSearchTerm) ||
            item.plantBrand?.toLowerCase().includes(lowerSearchTerm) ||
            item.plantVariety?.toLowerCase().includes(lowerSearchTerm)
          );
        }

        // Also check legacy single-plant order fields
        const matchesLegacyPlantName = order.plantName?.toLowerCase().includes(lowerSearchTerm) || false;
        const matchesLegacyPlantBrand = order.plantBrand?.toLowerCase().includes(lowerSearchTerm) || false;
        const matchesLegacyPlantVariety = order.plantVariety?.toLowerCase().includes(lowerSearchTerm) || false;

        matchesSearch = matchesCustomerName || matchesOrderId || matchesPlantInfo ||
                        matchesLegacyPlantName || matchesLegacyPlantBrand || matchesLegacyPlantVariety;
      }

      // Normalize status comparison: handle both "In Progress" and "in_progress" formats
      let matchesStatus = statusFilter === 'all';
      if (!matchesStatus) {
        const orderStatusNormalized = order.status.name.toLowerCase().replace(/\s+/g, '_');
        const filterStatusNormalized = statusFilter.toLowerCase().replace(/\s+/g, '_');
        matchesStatus = orderStatusNormalized === filterStatusNormalized;
      }

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      // Sort by order date in descending order (newest first)
      const dateA = formatDate(a.orderDate);
      const dateB = formatDate(b.orderDate);
      return dateB.getTime() - dateA.getTime();
    });

  const calculateTotalAmount = () => {
    if (!formData.plantId || !formData.quantity) return 0;
    const plant = plants.find(p => p.id === formData.plantId);
    if (!plant) return 0;
    // Use custom price if set, otherwise use plant's default price
    const priceToUse = customPricePerPlant ? parseFloat(customPricePerPlant) : plant.pricePerPlant;
    return parseInt(formData.quantity) * priceToUse;
  };

  const validatePhoneNumber = (phone: string) => {
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      return 'Phone number must be at least 10 digits';
    }
    if (cleanPhone.length > 15) {
      return 'Phone number cannot exceed 15 digits';
    }
    return null;
  };

  const validateDistance = (distance: string) => {
    if (!distance) return null; // Optional field
    const distanceNum = parseFloat(distance);
    if (isNaN(distanceNum)) {
      return 'Distance must be a valid number';
    }
    if (distanceNum <= 0) {
      return 'Distance must be greater than 0';
    }
    if (distance.length > 3) {
      return 'Distance cannot exceed 3 digits';
    }
    return null;
  };

  // Cart helper functions
  const generateItemId = () => `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const addToCart = () => {
    const plant = plants.find(p => p.id === formData.plantId);
    if (!plant || !formData.quantity) return;

    const quantity = parseInt(formData.quantity);
    // Use custom price if set, otherwise use plant's default price
    const priceToUse = customPricePerPlant ? parseFloat(customPricePerPlant) : plant.pricePerPlant;
    const totalPrice = quantity * priceToUse;
    const traysRequired = Math.ceil(quantity / plant.seedsPerTray);

    const newItem: SaleItem = {
      id: generateItemId(),
      plantId: plant.id,
      plantName: plant.name,
      plantBrand: plant.brand,
      plantVariety: plant.variety,
      pricePerPlant: priceToUse,
      quantity: quantity,
      totalPrice: totalPrice,
      seedsPerTray: plant.seedsPerTray,
      traysRequired: traysRequired,
    };

    setCartItems([...cartItems, newItem]);
    // Reset plant selection, quantity, and custom price after adding to cart
    setFormData({ ...formData, plantId: '', quantity: '' });
    setTraysInput('');
    setCustomPricePerPlant('');
    setQuantityInputMode('quantity');
    setShowAddPlantForm(false);
    setNewPlantData({
      name: '',
      brand: '',
      variety: '',
      pricePerPlant: '',
      growthDuration: '',
      seedsPerTray: '126',
      seedsProvider: 'Company',
    });
  };

  const removeFromCart = (itemId: string) => {
    setCartItems(cartItems.filter(item => item.id !== itemId));
  };

  const calculateCartTotal = () => {
    return {
      totalAmount: cartItems.reduce((sum, item) => sum + item.totalPrice, 0),
      totalTrays: cartItems.reduce((sum, item) => sum + item.traysRequired, 0),
      totalQuantity: cartItems.reduce((sum, item) => sum + item.quantity, 0),
    };
  };

  const filteredFarmers = customers.filter(customer =>
    customer.name.toLowerCase().includes(farmerSearchTerm.toLowerCase()) ||
    customer.phone.includes(farmerSearchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-white shadow-xl">
        <h1 className="text-3xl font-bold mb-2">Order Management</h1>
        <p className="text-blue-100 text-lg">Create and track orders for your farmers</p>
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl shadow-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 transform hover:scale-105"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Order
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by farmer, order ID, plant name, brand, variety..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
          >
            <option value="all">All Status</option>
            <option value="seedingpending">Seeding Pending</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="ready">Ready</option>
            <option value="delivered">Delivered</option>
            <option value="complete">Complete</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        {/* View Toggle */}
        <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'grid'
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <LayoutGrid className="h-4 w-4" />
            Grid
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'list'
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <List className="h-4 w-4" />
            List
          </button>
        </div>
      </div>

      {/* Order Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 sm:top-20 mx-auto p-5 border w-11/12 max-w-md sm:max-w-2xl shadow-lg rounded-md bg-white mb-24">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Order</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Select Farmer</label>
                    <button
                      type="button"
                      onClick={() => setShowAddFarmerForm(!showAddFarmerForm)}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium text-green-700 bg-green-100 border border-green-300 rounded-md hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add New Farmer
                    </button>
                  </div>
                  
                  {!showAddFarmerForm && (
                    <>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Type farmer name or phone to search..."
                          value={farmerSearchTerm}
                          onChange={(e) => {
                            setFarmerSearchTerm(e.target.value);
                            if (formData.customerId) {
                              setFormData({ ...formData, customerId: '' });
                            }
                          }}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                        />
                        {formData.customerId ? (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, customerId: '' });
                              setFarmerSearchTerm('');
                            }}
                            className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 hover:text-gray-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        ) : (
                          <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                        )}
                        
                        {/* Search Results Dropdown */}
                        {farmerSearchTerm && !formData.customerId && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {filteredFarmers.length > 0 ? (
                              filteredFarmers.map(customer => (
                                <div
                                  key={customer.id}
                                  onClick={() => {
                                    setFormData({ ...formData, customerId: customer.id });
                                    setFarmerSearchTerm(`${customer.name} - ${customer.phone}`);
                                  }}
                                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="font-medium text-gray-900">{customer.name}</div>
                                  <div className="text-sm text-gray-500">
                                    {customer.phone}
                                    {customer.distanceKm ? ` • ${customer.distanceKm}km away` : ''}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                No farmers found. Try a different search term.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Hidden input for form validation */}
                      <input
                        type="hidden"
                        required={!showAddFarmerForm}
                        value={formData.customerId}
                      />
                      
                      {formData.customerId && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                          <div className="text-sm text-green-700">
                            ✓ Selected: {customers.find(c => c.id === formData.customerId)?.name}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* New Farmer Form */}
                {showAddFarmerForm && (
                  <div className="bg-gradient-to-br from-green-50 to-blue-50 border border-green-200 rounded-xl p-6 space-y-4 shadow-sm">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <Plus className="h-4 w-4 text-green-600" />
                      </div>
                      <h4 className="text-lg font-semibold text-green-900">New Farmer Registration</h4>
                    </div>
                    {loading && (
                      <div className="flex items-center justify-center py-3 bg-white rounded-lg">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                        <span className="ml-3 text-sm text-green-700 font-medium">Creating farmer profile...</span>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Farmer Name *</label>
                        <input
                          type="text"
                          required
                          value={newFarmerData.name}
                          onChange={(e) => setNewFarmerData({ ...newFarmerData, name: e.target.value })}
                          className="block w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm"
                          placeholder="Enter farmer name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          required
                          maxLength={15}
                          value={newFarmerData.phone}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers (no letters, decimals, or special characters except +)
                            if (value === '' || /^[+]?[\d\s-]*$/.test(value)) {
                              setNewFarmerData({ ...newFarmerData, phone: value.replace(/[^\d+]/g, '') });
                            }
                          }}
                          className="block w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm"
                          placeholder="Enter phone number"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
                      <textarea
                        required
                        value={newFarmerData.address}
                        onChange={(e) => setNewFarmerData({ ...newFarmerData, address: e.target.value })}
                        className="block w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm resize-none"
                        placeholder="Enter complete address"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Distance from Polyhouse</label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          max="999"
                          value={newFarmerData.distanceKm}
                          onChange={(e) => {
                            if (e.target.value.length <= 3) {
                              setNewFarmerData({ ...newFarmerData, distanceKm: e.target.value });
                            }
                          }}
                          onWheel={(e) => e.currentTarget.blur()}
                          className="block w-full border border-gray-300 rounded-lg px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm"
                          placeholder="12.5"
                        />
                        <span className="absolute right-4 top-3 text-sm text-gray-500">km</span>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">Optional - helps with delivery planning</p>
                    </div>
                  </div>
                )}
                {/* Add Plant to Cart Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">Select Plant</label>
                      {!showAddPlantForm && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddPlantForm(true);
                            setFormData({ ...formData, plantId: '' });
                          }}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                        >
                          <Sprout className="h-4 w-4 mr-1.5" />
                          Add New Plant
                        </button>
                      )}
                    </div>

                    {!showAddPlantForm ? (
                      <>
                        <select
                          value={formData.plantId}
                          onChange={(e) => setFormData({ ...formData, plantId: e.target.value })}
                          className="block w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          <option value="">Choose a plant</option>
                          {plants.filter(p => p.isActive).map(plant => (
                            <option key={plant.id} value={plant.id}>
                              {plant.name} ({plant.brand}{plant.variety ? ` - ${plant.variety}` : ''}) • {plant.seedsPerTray}/tray • ₹{plant.pricePerPlant}
                            </option>
                          ))}
                        </select>
                        {/* Display selected plant details and price override */}
                        {formData.plantId && (() => {
                          const selectedPlant = plants.find(p => p.id === formData.plantId);
                          if (!selectedPlant) return null;
                          return (
                            <div className="mt-3 space-y-3">
                              {/* Plant Info Card */}
                              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="flex items-center gap-2">
                                    <Package className="h-4 w-4 text-green-600" />
                                    <div>
                                      <span className="text-xs text-gray-500">Tray Capacity</span>
                                      <p className="text-sm font-bold text-gray-900">{selectedPlant.seedsPerTray} plants/tray</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <IndianRupee className="h-4 w-4 text-green-600" />
                                    <div>
                                      <span className="text-xs text-gray-500">Default Price</span>
                                      <p className="text-sm font-bold text-gray-900">₹{selectedPlant.pricePerPlant}/plant</p>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Price Override Section */}
                              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <label className="flex items-center gap-2 text-sm font-medium text-amber-800 mb-2">
                                      <IndianRupee className="h-4 w-4" />
                                      Custom Price per Plant
                                      <span className="text-xs font-normal text-amber-600">(Optional)</span>
                                    </label>
                                    <div className="relative">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0.01"
                                        value={customPricePerPlant}
                                        onChange={(e) => setCustomPricePerPlant(e.target.value)}
                                        onWheel={(e) => e.currentTarget.blur()}
                                        className="w-full pl-7 pr-3 py-2 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white"
                                        placeholder={`${selectedPlant.pricePerPlant} (default)`}
                                      />
                                    </div>
                                    <p className="mt-1 text-xs text-amber-600">
                                      Leave empty to use default price. Custom price only applies to this order.
                                    </p>
                                  </div>
                                  {customPricePerPlant && (
                                    <button
                                      type="button"
                                      onClick={() => setCustomPricePerPlant('')}
                                      className="mt-7 p-1.5 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded-lg transition-colors"
                                      title="Reset to default price"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                                {customPricePerPlant && parseFloat(customPricePerPlant) !== selectedPlant.pricePerPlant && (
                                  <div className="mt-3 flex items-center gap-2 p-2 bg-white rounded-lg border border-amber-200">
                                    <span className="text-xs text-gray-500">Price changed:</span>
                                    <span className="text-xs text-gray-400 line-through">₹{selectedPlant.pricePerPlant}</span>
                                    <span className="text-xs font-bold text-amber-700">→ ₹{customPricePerPlant}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <Sprout className="h-5 w-5 text-green-600 mr-2" />
                            <span className="font-medium text-green-800">New Plant Details</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setShowAddPlantForm(false);
                              setNewPlantData({
                                name: '',
                                brand: '',
                                variety: '',
                                pricePerPlant: '',
                                growthDuration: '',
                                seedsPerTray: '126',
                                seedsProvider: 'Company',
                              });
                            }}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Plant Name *</label>
                            <input
                              type="text"
                              value={newPlantData.name}
                              onChange={(e) => setNewPlantData({ ...newPlantData, name: e.target.value })}
                              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="e.g., Cherry Tomato"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Brand *</label>
                            <input
                              type="text"
                              value={newPlantData.brand}
                              onChange={(e) => setNewPlantData({ ...newPlantData, brand: e.target.value })}
                              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="e.g., Syngenta"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Variety *</label>
                            <input
                              type="text"
                              value={newPlantData.variety}
                              onChange={(e) => setNewPlantData({ ...newPlantData, variety: e.target.value })}
                              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="e.g., Hybrid F1"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Price per Plant (₹) *</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0.01"
                              value={newPlantData.pricePerPlant}
                              onChange={(e) => setNewPlantData({ ...newPlantData, pricePerPlant: e.target.value })}
                              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="5.50"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Growth Duration (days) *</label>
                            <input
                              type="number"
                              min="1"
                              value={newPlantData.growthDuration}
                              onChange={(e) => setNewPlantData({ ...newPlantData, growthDuration: e.target.value })}
                              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="30"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Seeds per Tray *</label>
                            <input
                              type="number"
                              min="1"
                              value={newPlantData.seedsPerTray}
                              onChange={(e) => setNewPlantData({ ...newPlantData, seedsPerTray: e.target.value })}
                              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              placeholder="126"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Seeds Provider *</label>
                            <select
                              value={newPlantData.seedsProvider}
                              onChange={(e) => setNewPlantData({ ...newPlantData, seedsProvider: e.target.value as 'Farmer' | 'Company' })}
                              className="block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            >
                              <option value="Company">Company</option>
                              <option value="Farmer">Farmer</option>
                            </select>
                          </div>
                        </div>
                        <div className="mt-4">
                          <button
                            type="button"
                            onClick={async () => {
                              // Validate new plant data
                              if (!newPlantData.name || !newPlantData.brand || !newPlantData.variety ||
                                  !newPlantData.pricePerPlant || !newPlantData.growthDuration || !newPlantData.seedsPerTray) {
                                alert('Please fill in all required plant details.');
                                return;
                              }

                              // Check for duplicate plant (same brand + variety + seedsPerTray)
                              const duplicatePlant = plants.find(plant => {
                                const isSameBrand = plant.brand.toLowerCase().trim() === newPlantData.brand.toLowerCase().trim();
                                const isSameVariety = (plant.variety || '').toLowerCase().trim() === newPlantData.variety.toLowerCase().trim();
                                const isSameTrayCapacity = plant.seedsPerTray === parseInt(newPlantData.seedsPerTray);
                                return isSameBrand && isSameVariety && isSameTrayCapacity;
                              });

                              if (duplicatePlant) {
                                alert(`A plant with brand "${newPlantData.brand}", variety "${newPlantData.variety}", and ${newPlantData.seedsPerTray} seeds/tray already exists: ${duplicatePlant.name}`);
                                return;
                              }

                              try {
                                setLoading(true);
                                const plantData = {
                                  name: newPlantData.name,
                                  brand: newPlantData.brand,
                                  variety: newPlantData.variety,
                                  pricePerPlant: parseFloat(newPlantData.pricePerPlant),
                                  growthDuration: parseInt(newPlantData.growthDuration),
                                  seedsPerTray: parseInt(newPlantData.seedsPerTray),
                                  seedsProvider: newPlantData.seedsProvider,
                                  availability: 0,
                                  isActive: true,
                                };
                                const newPlantId = await plantService.addPlant(plantData);

                                const newPlant: Plant = {
                                  id: newPlantId,
                                  ...plantData,
                                  createdAt: new Date() as any,
                                };

                                setPlants([newPlant, ...plants]);
                                setFormData({ ...formData, plantId: newPlantId });
                                setShowAddPlantForm(false);
                                setNewPlantData({
                                  name: '',
                                  brand: '',
                                  variety: '',
                                  pricePerPlant: '',
                                  growthDuration: '',
                                  seedsPerTray: '126',
                                  seedsProvider: 'Company',
                                });
                              } catch (error) {
                                console.error('Error creating new plant:', error);
                                alert('Error creating new plant. Please try again.');
                              } finally {
                                setLoading(false);
                              }
                            }}
                            disabled={loading}
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                          >
                            {loading ? 'Creating Plant...' : 'Create Plant & Select'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Quantity Input Section */}
                    <div className="space-y-3">
                      {/* Input Mode Toggle */}
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">Enter by</label>
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                          <button
                            type="button"
                            onClick={() => {
                              setQuantityInputMode('quantity');
                              setTraysInput('');
                            }}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                              quantityInputMode === 'quantity'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Plants
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setQuantityInputMode('trays');
                              setFormData({ ...formData, quantity: '' });
                            }}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                              quantityInputMode === 'trays'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            Trays
                          </button>
                        </div>
                      </div>

                      {/* Quantity Input (Plants) */}
                      {quantityInputMode === 'quantity' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Number of Plants</label>
                          <input
                            type="number"
                            min="200"
                            max="1000000"
                            step="1"
                            maxLength={7}
                            value={formData.quantity}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (/^\d+$/.test(value) && value.length <= 7)) {
                                setFormData({ ...formData, quantity: value });
                              }
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                            className="block w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter number of plants (min 200)"
                          />
                        </div>
                      )}

                      {/* Trays Input */}
                      {quantityInputMode === 'trays' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Number of Trays</label>
                          <input
                            type="number"
                            min="1"
                            max="10000"
                            step="1"
                            value={traysInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || (/^\d+$/.test(value) && parseInt(value) <= 10000)) {
                                setTraysInput(value);
                                // Calculate quantity from trays
                                if (value && formData.plantId) {
                                  const plant = plants.find(p => p.id === formData.plantId);
                                  if (plant) {
                                    const calculatedQuantity = parseInt(value) * plant.seedsPerTray;
                                    setFormData({ ...formData, quantity: calculatedQuantity.toString() });
                                  }
                                } else {
                                  setFormData({ ...formData, quantity: '' });
                                }
                              }
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                            className="block w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter number of trays"
                          />
                        </div>
                      )}

                      {/* Calculated Values Display */}
                      {formData.plantId && (formData.quantity || traysInput) && (() => {
                        const selectedPlant = plants.find(p => p.id === formData.plantId);
                        const effectivePrice = customPricePerPlant ? parseFloat(customPricePerPlant) : (selectedPlant?.pricePerPlant || 0);
                        const isCustomPrice = customPricePerPlant && parseFloat(customPricePerPlant) !== selectedPlant?.pricePerPlant;
                        return (
                          <div className="bg-white rounded-lg border border-blue-200 p-3 space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600 flex items-center gap-1.5">
                                <Sprout className="h-4 w-4 text-green-500" />
                                Total Plants
                              </span>
                              <span className="font-semibold text-gray-900">
                                {formData.quantity ? parseInt(formData.quantity).toLocaleString() : '0'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600 flex items-center gap-1.5">
                                <Package className="h-4 w-4 text-blue-500" />
                                Trays Required
                              </span>
                              <span className="font-semibold text-gray-900">
                                {formData.quantity
                                  ? Math.ceil(parseInt(formData.quantity) / (selectedPlant?.seedsPerTray || 126))
                                  : '0'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600 flex items-center gap-1.5">
                                <IndianRupee className="h-4 w-4 text-amber-500" />
                                Price/Plant
                              </span>
                              <span className={`font-semibold ${isCustomPrice ? 'text-amber-600' : 'text-gray-900'}`}>
                                ₹{effectivePrice}
                                {isCustomPrice && <span className="ml-1 text-xs">(custom)</span>}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100">
                              <span className="text-gray-600 font-medium">Item Total</span>
                              <span className="font-bold text-green-600 text-base">
                                ₹{calculateTotalAmount().toLocaleString()}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    <button
                      type="button"
                      onClick={addToCart}
                      disabled={!formData.plantId || !formData.quantity || parseInt(formData.quantity) < 200}
                      className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add to Order
                    </button>
                    {formData.plantId && formData.quantity && parseInt(formData.quantity) < 200 && (
                      <p className="text-xs text-amber-600 text-center mt-1">Minimum 200 plants required</p>
                    )}
                  </div>
                </div>

                {/* Cart Items Display */}
                {cartItems.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Order Items ({cartItems.length})
                    </h4>

                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {cartItems.map((item, index) => {
                        const originalPlant = plants.find(p => p.id === item.plantId);
                        const isCustomPrice = originalPlant && item.pricePerPlant !== originalPlant.pricePerPlant;
                        return (
                          <div key={item.id} className="bg-white rounded-lg p-3 border border-green-100 flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-gray-400">#{index + 1}</span>
                                <span className="font-medium text-gray-900">{item.plantName}</span>
                                {isCustomPrice && (
                                  <span className="px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                                    Custom ₹{item.pricePerPlant}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {item.plantBrand}{item.plantVariety ? ` - ${item.plantVariety}` : ''} • {item.quantity.toLocaleString()} plants • {item.traysRequired} trays
                              </div>
                              <div className="text-sm font-medium text-green-600 mt-1">
                                ₹{item.totalPrice.toLocaleString()}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFromCart(item.id)}
                              className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {/* Cart Totals */}
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Items:</span>
                        <span className="font-medium">{cartItems.length}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Trays:</span>
                        <span className="font-medium">{calculateCartTotal().totalTrays}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Plants:</span>
                        <span className="font-medium">{calculateCartTotal().totalQuantity.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold mt-2 pt-2 border-t border-green-200">
                        <span className="text-gray-900">Grand Total:</span>
                        <span className="text-green-600">₹{calculateCartTotal().totalAmount.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Expected Delivery Date - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="inline h-4 w-4 mr-1.5 text-gray-500" />
                    Expected Delivery Date
                    <span className="text-gray-400 font-normal ml-1">(Optional)</span>
                  </label>
                  <input
                    type="date"
                    value={formData.expectedDeliveryDate}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setFormData({ ...formData, expectedDeliveryDate: e.target.value })}
                    className="block w-full border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">Leave empty if delivery date is not yet decided</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount Paid (₹)</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    max={cartItems.length > 0 ? calculateCartTotal().totalAmount : undefined}
                    maxLength={7}
                    value={formData.amountPaid}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow integers (no decimals)
                      if (value === '' || (/^\d+$/.test(value) && value.length <= 7)) {
                        setFormData({ ...formData, amountPaid: value });
                        // Clear error when user starts typing
                        if (amountPaidError) {
                          setAmountPaidError(null);
                        }
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    onBlur={(e) => {
                      // Validate on blur against cart total
                      const cartTotal = calculateCartTotal().totalAmount;
                      const amountPaid = parseFloat(e.target.value) || 0;
                      // Only validate against cart total if cart has items
                      if (cartItems.length > 0 && amountPaid > cartTotal) {
                        setAmountPaidError(`Amount paid cannot exceed total amount (₹${cartTotal.toLocaleString()})`);
                      } else if (amountPaid > 0 && amountPaid < 500) {
                        setAmountPaidError('Minimum amount paid must be ₹500');
                      } else {
                        setAmountPaidError(null);
                      }
                    }}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500 ${
                      amountPaidError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0 (min ₹500)"
                  />
                  {amountPaidError && (
                    <p className="mt-1 text-sm text-red-600">{amountPaidError}</p>
                  )}
                  {cartItems.length > 0 && !amountPaidError && (
                    <p className="mt-1 text-sm text-gray-500">
                      Total Amount: ₹{calculateCartTotal().totalAmount.toLocaleString()} |
                      Min Payment: ₹500 |
                      Max Payment: ₹{calculateCartTotal().totalAmount.toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Creating...' : 'Create Order'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 sm:top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white mb-8">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Change Order Status</h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  Order: <span className="font-medium">{selectedOrder.customerName}</span>
                </p>
                <p className="text-sm text-gray-600">
                  Current Status: <span className="font-medium">{selectedOrder.status.name}</span>
                </p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">New Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                >
                  {statusOptions.map(status => (
                    <option key={status.id} value={status.id}>
                      {status.name} - {status.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowStatusModal(false);
                    setSelectedOrder(null);
                    setNewStatus('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusChange}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* List View - Card Format (Default) */}
      {viewMode === 'list' && (
        <div className="space-y-3">
          {/* Header Row - Hidden on mobile */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-4 px-5 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <div className="col-span-3">Customer & Order</div>
            <div className="col-span-2">Plant Details</div>
            <div className="col-span-1 text-center">Quantity</div>
            <div className="col-span-1 text-center">Amount</div>
            <div className="col-span-2 text-center">Payment</div>
            <div className="col-span-1 text-center">Status</div>
            <div className="col-span-1 text-center">Delivery</div>
            <div className="col-span-1 text-center">Actions</div>
          </div>

          {filteredOrders.map((order) => {
            // Get unique plant names for multi-plant orders
            const uniquePlantNames = order.items && order.items.length > 0
              ? Array.from(new Set(order.items.map(item => item.plantName)))
              : [];

            return (
              <div key={order.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200">
                <div className="p-4 lg:py-3">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-4 items-center">

                    {/* Customer & Order Info */}
                    <div className="lg:col-span-3 flex items-start gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                        <Package className="h-5 w-5 text-white" />
                      </div>
                      <div className="min-w-0 flex-1 text-left space-y-0.5">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate" title={order.customerName}>
                          {order.customerName}
                        </h3>
                        <div className="text-xs font-mono text-blue-600 dark:text-blue-400">
                          #{order.orderId || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {format(formatDate(order.orderDate), 'dd MMM yyyy')}
                        </div>
                      </div>
                    </div>

                    {/* Plant Details */}
                    <div className="lg:col-span-2 min-w-0">
                      {order.items && order.items.length > 0 ? (
                        <div>
                          <div className="flex items-center gap-2">
                            <Sprout className="h-4 w-4 text-green-500 flex-shrink-0" />
                            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                              {order.items.length === 1 ? order.items[0].plantName : `${order.items.length} Plants`}
                            </span>
                          </div>
                          {order.items.length === 1 ? (
                            <div className="flex flex-wrap gap-1 mt-1.5 ml-6">
                              {order.items[0].plantBrand && (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 rounded-md">
                                  {order.items[0].plantBrand}
                                </span>
                              )}
                              {order.items[0].plantVariety && (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 rounded-md">
                                  {order.items[0].plantVariety}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-500 dark:text-gray-400 ml-6 mt-1 truncate" title={uniquePlantNames.join(', ')}>
                              {uniquePlantNames.slice(0, 2).join(', ')}{uniquePlantNames.length > 2 ? ` +${uniquePlantNames.length - 2}` : ''}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center gap-2">
                            <Sprout className="h-4 w-4 text-green-500 flex-shrink-0" />
                            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                              {order.plantName || '-'}
                            </span>
                          </div>
                          {order.plantBrand && (
                            <div className="flex flex-wrap gap-1 mt-1.5 ml-6">
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 rounded-md">
                                {order.plantBrand}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Quantity */}
                    <div className="lg:col-span-1 text-center">
                      <div className="lg:hidden text-xs text-gray-500 mb-1">Quantity</div>
                      <div className="text-sm font-bold text-gray-900 dark:text-white">
                        {order.items && order.items.length > 0
                          ? order.items.reduce((sum, item) => sum + item.quantity, 0).toLocaleString()
                          : (order.quantity?.toLocaleString() || '-')}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {order.totalTrays} trays
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="lg:col-span-1 text-center">
                      <div className="lg:hidden text-xs text-gray-500 mb-1">Amount</div>
                      <div className="text-sm font-bold text-gray-900 dark:text-white">
                        ₹{order.totalAmount.toLocaleString()}
                      </div>
                    </div>

                    {/* Payment */}
                    <div className="lg:col-span-2 text-center">
                      <div className="lg:hidden text-xs text-gray-500 mb-1">Payment</div>
                      <div className="text-sm font-semibold text-green-600 dark:text-green-400">
                        ₹{order.amountPaid.toLocaleString()}
                      </div>
                      {order.remainingAmount > 0 && (
                        <div className="text-xs font-medium text-red-500 dark:text-red-400 mt-0.5">
                          Due: ₹{order.remainingAmount.toLocaleString()}
                        </div>
                      )}
                      {order.remainingAmount === 0 && (
                        <div className="text-xs font-medium text-green-500 dark:text-green-400 mt-0.5">
                          Paid Full
                        </div>
                      )}
                    </div>

                    {/* Status */}
                    <div className="lg:col-span-1 text-center">
                      <div className="lg:hidden text-xs text-gray-500 mb-1">Status</div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold ${getStatusColor(order.status.name)}`}>
                        {order.status.name}
                      </span>
                    </div>

                    {/* Delivery */}
                    <div className="lg:col-span-1 text-center">
                      <div className="lg:hidden text-xs text-gray-500 mb-1">Delivery</div>
                      {(order as any).expectedDeliveryDate ? (
                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                          <Calendar className="h-3 w-3 text-gray-500" />
                          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            {format(formatDate((order as any).expectedDeliveryDate), 'dd MMM')}
                          </span>
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 bg-gray-50 dark:bg-gray-700/50 text-gray-400 dark:text-gray-500 rounded-lg text-xs">
                          Not Set
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="lg:col-span-1 flex flex-col items-center justify-center gap-1.5">
                      <button
                        onClick={() => {
                          setSelectedOrderForDetails(order);
                          setShowOrderDetails(true);
                        }}
                        className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        Details
                      </button>
                      <button
                        onClick={() => openStatusModal(order)}
                        className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        <Edit className="h-3.5 w-3.5 mr-1" />
                        Status
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Grid View - Order Cards */}
      {viewMode === 'grid' && (
        <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
          {filteredOrders.map((order) => (
            <div key={order.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Package className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-gray-900">{order.customerName}</h3>
                      <p className="text-sm text-gray-500">Order #{order.orderId || 'N/A'}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status.name)}`}>
                    {order.status.name}
                  </span>
                </div>

                {/* Plant Info */}
                {(order.items && order.items.length > 0) || order.plantName ? (
                  <div className="mb-4 p-4 bg-white dark:bg-gray-800 rounded-xl border-2 border-green-200 dark:border-green-700 shadow-sm">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                        <Sprout className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-xs font-bold text-green-700 dark:text-green-400 uppercase tracking-wide">
                        {order.items && order.items.length > 1 ? `${order.items.length} Plants` : 'Plant Details'}
                      </span>
                    </div>
                    {order.items && order.items.length > 0 ? (
                      <div className="space-y-2">
                        {order.items.map((item, index) => (
                          <div key={item.id || index} className="text-sm bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-800 dark:text-white">{item.plantName}</span>
                              <span className="text-gray-700 dark:text-gray-300 font-medium">{item.quantity.toLocaleString()} plants</span>
                            </div>
                            {(item.plantBrand || item.plantVariety) && (
                              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                {item.plantBrand}{item.plantVariety ? ` - ${item.plantVariety}` : ''}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Name</span>
                          <span className="text-sm font-bold text-gray-800 dark:text-white">{order.plantName}</span>
                        </div>
                        {order.plantBrand && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Brand</span>
                            <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">{order.plantBrand}</span>
                          </div>
                        )}
                        {order.plantVariety && (
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Variety</span>
                            <span className="text-sm font-semibold text-purple-700 dark:text-purple-400">{order.plantVariety}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Order Details */}
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Order Date</span>
                    <span className="text-sm font-medium text-gray-900">
                      {format(formatDate(order.orderDate), 'MMM dd, yyyy')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Quantity / Trays</span>
                    <span className="text-sm font-medium text-gray-900">
                      {order.items && order.items.length > 0
                        ? order.items.reduce((sum, item) => sum + item.quantity, 0).toLocaleString()
                        : (order.quantity?.toLocaleString() || '-')} / {order.totalTrays}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Amount</span>
                    <span className="text-lg font-bold text-gray-900">₹{order.totalAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Paid</span>
                    <span className="text-sm font-medium text-green-600">₹{order.amountPaid.toLocaleString()}</span>
                  </div>
                  {order.remainingAmount > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Remaining</span>
                      <span className="text-sm font-medium text-red-600">₹{order.remainingAmount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Expected Delivery</span>
                    <span className="text-sm font-medium text-gray-900">
                      {(order as any).expectedDeliveryDate ?
                        format(formatDate((order as any).expectedDeliveryDate), 'MMM dd, yyyy') :
                        'Not Set'
                      }
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setSelectedOrderForDetails(order);
                      setShowOrderDetails(true);
                    }}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-200 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Details
                  </button>
                  <button
                    onClick={() => openStatusModal(order)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Status
                  </button>
                  <a
                    href={`tel:${customers.find(c => c.id === order.customerId)?.phone || ''}`}
                    className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-green-700 bg-green-100 border border-green-200 rounded-lg hover:bg-green-200 transition-colors"
                    title="Call Farmer"
                  >
                    <Phone className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredOrders.length === 0 && (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' ? 'Try adjusting your search or filter criteria.' : 'Get started by creating a new order.'}
          </p>
        </div>
      )}

      {/* Receipt Generator */}
      {showReceipt && receiptData && (
        <ReceiptGenerator
          order={receiptData.order}
          customer={receiptData.customer}
          payments={receiptData.payments}
          plantDetails={receiptData.plantDetails}
          receiptType={receiptData.receiptType}
          onClose={() => {
            setShowReceipt(false);
            setReceiptData(null);
          }}
          onPrint={async () => {
            await printReceiptWithLogo(
              receiptData.order,
              receiptData.customer,
              receiptData.payments,
              receiptData.receiptType,
              receiptData.plantDetails
            );
          }}
        />
      )}

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrderForDetails}
        isOpen={showOrderDetails}
        onClose={() => {
          setShowOrderDetails(false);
          setSelectedOrderForDetails(null);
        }}
        onOrderUpdate={(updatedOrder) => {
          // Update the orders list if order status changes
          setOrders(orders.map(order => 
            order.id === updatedOrder.id ? updatedOrder : order
          ));
        }}
      />
    </div>
  );
};

export default OrderManagement; 